import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { EllipsisVertical, IdCard, KeyRound, UserRound } from "lucide-react";
import { useState } from "react";

import { Avatar } from "@/components/avatar";
import { EditProfileDialog } from "@/components/edit-profile-dialog";
import { Card } from "@/components/ui/card";
import { Menu, menuItemClass } from "@/components/ui/menu";
import { Skeleton } from "@/components/ui/skeleton";
import { ROUTES } from "@/constants/routes";
import { profileQuery } from "@/services/queries";
import { useTranslation } from "@/store/locale";

/**
 * Who is signed in, as the dashboard's own record: picture, name, employee
 * number, the club they are posted to, and the day the account was last let in.
 *
 * It is the one card here that is about a person rather than a destination, so
 * it carries a portrait rather than an icon chip, and its only action sits
 * behind the ellipsis — the card is a fact, not a button.
 */
export function DashboardProfile() {
  const t = useTranslation();
  const { data: profile, isLoading } = useQuery(profileQuery());
  const [editing, setEditing] = useState(false);

  if (isLoading) return <Skeleton className="rounded-card h-[220px]" />;
  // Same rule as the figures above: a profile that will not load leaves the
  // page one card shorter rather than showing an apology nobody can act on.
  if (!profile) return null;

  const name = profile.full_name || profile.user_name;
  // The posting in effect today is the club they are actually at.
  const club =
    (profile.has_active_temporary_club ? profile.temporary_club : profile.club)?.club_name ??
    null;
  const visited = profile.last_login_at
    ? t("dash.visitedOn").replace(
        "{date}",
        format(new Date(profile.last_login_at), "MM/dd/yyyy"),
      )
    : t("dash.noVisit");

  return (
    <Card className="flex items-start gap-4 p-4">
      {/* A portrait, as the legacy card had it — a person, not an avatar chip.
          It gives up height on a phone rather than crowding out the name. */}
      <Avatar
        src={profile.avatar}
        name={name}
        className="h-[150px] w-[108px] shrink-0 rounded-[14px] text-3xl sm:h-[186px] sm:w-[136px]"
      />
      <div className="min-w-0 flex-1">
        <h2 className="text-title-2 text-heading truncate">{name}</h2>
        <p className="text-fg text-callout mt-1 truncate font-semibold tabular-nums">
          {profile.user_name}
        </p>
        {club ? <p className="text-fg-muted text-callout mt-1 truncate">{club}</p> : null}
        <p className="text-fg-subtle text-subhead mt-3">{visited}</p>
      </div>
      <Menu
        width="w-48"
        trigger={
          <span
            title={t("dash.profileActions")}
            className="text-fg-subtle hover:bg-surface-inset hover:text-fg grid size-8 place-items-center rounded-lg transition-colors"
          >
            <EllipsisVertical className="size-4" aria-hidden />
            <span className="sr-only">{t("dash.profileActions")}</span>
          </span>
        }
      >
        {/* A sheet, as legacy had it: what a person edits about themselves is
            five fields, and the dashboard is where they were already standing.
            The profile page remains, and remains the place the whole account
            is read — postings, roles, security. */}
        <button type="button" className={menuItemClass()} onClick={() => setEditing(true)}>
          <UserRound aria-hidden />
          {t("shell.editProfile")}
        </button>
        <Link to={ROUTES.PROFILE} className={menuItemClass()}>
          <IdCard aria-hidden />
          {t("shell.viewProfile")}
        </Link>
        <Link to={ROUTES.PROFILE_PASSWORD} className={menuItemClass()}>
          <KeyRound aria-hidden />
          {t("shell.changePassword")}
        </Link>
      </Menu>

      {/* Mounted only while it is open, so each visit starts from the account
          as it stands rather than from a draft left behind by the last one.
          Deliberately not keyed on `updated_at`: the picture saves on its own,
          which moves that timestamp, and a remount mid-edit would throw away
          the name and note the person is part-way through typing. */}
      {editing ? (
        <EditProfileDialog profile={profile} open onClose={() => setEditing(false)} />
      ) : null}
    </Card>
  );
}
